import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { IoCloseOutline } from 'react-icons/io5';
import { CModalFooter } from '@coreui/react';
import LoadingAvatar from '../../../components/LoadingAvatar';
import {
  BOOKING_DETAIL_MODAL_PROPS,
  StyledModalBodyV2,
  StyledModalCardV2
} from '../../../components/modalV2';
import { LOADING_BLUE } from '../../../utils/colorCode';
import { Palette } from '../../../components/utils';
import { GET_BOOKING_V3 } from '../../../redux/constants';
import {
  clearBookingError,
  confirmBookingV3,
  getBookingV3,
  getBookingsCalendarV3,
  getBookingsListV3
} from '../../../redux/actions/bookingParAm';
import { changeOpenedModal } from '../../../redux/actions/headerModal';
import { setToast } from '../../../redux/actions/window';
import { getAmenityV2, getBuildingAmenitiesV2 } from '../../../redux/actions/amenities';
import { requestPending, requestFail } from '../../../utils/status';
import {
  canShowApproveBooking,
  canShowCancelBooking,
  canShowEditBooking,
  getAmenityCoverImageUrl,
  getBookingDetailHeaderTitle,
  getBookingDetailStatusLabel,
  isApproveBookingDisabled,
  isBookingBlock
} from './bookingDetailDisplayHelpers';
import { buildListV3Params } from './bookingsFilterHelpers';
import BookingDetailsV2Content, { BookingDetailStatusBadge } from './BookingDetailsV2Content';
import {
  ApproveBookingButton,
  CancelBookingButton,
  DetailCloseButton,
  DetailHeaderActions,
  DetailHeaderBlock,
  DetailHeaderRow,
  DetailHeaderTitle
} from './bookingDetailsV2Styles';

const BookingDetailsV2Modal = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const { t } = useTranslation();

  const { booking, status, error } = useSelector((state) => state.parAmBooking);
  const { modal } = useSelector((state) => state.headerModal);
  const { amenity } = useSelector((state) => state.amenities);
  const { globalBuildingId } = useSelector((state) => state.building);

  const [bannerHidden, setBannerHidden] = useState(false);
  const [isApproving, setIsApproving] = useState(false);

  const isOpen = modal === 'bookingDetailsV2';
  const bookingPending = status === requestPending(GET_BOOKING_V3);
  const bookingFailed = status === requestFail(GET_BOOKING_V3);
  const bookingLoaded = Boolean(booking?.id) && !bookingPending && !bookingFailed;

  const amenityMatchesBooking =
    amenity?.id != null && booking?.amenity_id != null && Number(amenity.id) === Number(booking.amenity_id);

  const coverUrl = useMemo(() => {
    if (!amenityMatchesBooking) return null;
    return getAmenityCoverImageUrl(amenity);
  }, [amenity, amenityMatchesBooking]);

  const bannerUrl = coverUrl && !bannerHidden ? coverUrl : null;

  useEffect(() => {
    if (!isOpen || !bookingLoaded || !booking?.amenity_id) return;
    dispatch(getAmenityV2({ id: booking.amenity_id }));
  }, [isOpen, bookingLoaded, booking?.amenity_id, booking?.id, dispatch]);

  useEffect(() => {
    setBannerHidden(false);
  }, [booking?.id, coverUrl]);

  useEffect(() => {
    if (!isOpen) {
      setIsApproving(false);
    }
  }, [isOpen]);

  const onClose = useCallback(() => {
    dispatch(changeOpenedModal({ modal: '' }));
    dispatch(clearBookingError());
  }, [dispatch]);

  const handleBannerError = useCallback(() => {
    setBannerHidden(true);
  }, []);

  const handleViewAmenity = useCallback(() => {
    if (!booking?.amenity_id) return;
    const openDetail = () => {
      dispatch(
        changeOpenedModal({
          modal: 'amenityDetailV2',
          data: { amenityId: booking.amenity_id, returnModal: 'bookingDetailsV2' }
        })
      );
    };
    if (!amenityMatchesBooking) {
      dispatch(
        getAmenityV2({
          id: booking.amenity_id,
          success: openDetail
        })
      );
    } else {
      openDetail();
    }
  }, [amenityMatchesBooking, booking?.amenity_id, dispatch]);

  const handleCancelBooking = useCallback(() => {
    dispatch(
      changeOpenedModal({
        modal: 'bookingDetailsV2',
        cancelModal: true
      })
    );
  }, [dispatch]);

  const openEditModal = useCallback(
    (loadingModal, editModal) => {
      if (!booking?.id) return;
      const buildingId = booking.building_id ?? globalBuildingId;
      dispatch(changeOpenedModal({ modal: loadingModal }));
      dispatch(
        getBuildingAmenitiesV2({
          id: buildingId,
          success: () => {
            dispatch(
              getBookingV3({
                id: booking.id,
                success: () => {
                  dispatch(changeOpenedModal({ modal: editModal }));
                },
                fail: (err) => {
                  dispatch(
                    setToast({
                      toastShow: true,
                      toastMessage:
                        err?.data?.message ||
                        t('Failed to load booking!', { defaultValue: 'Failed to load booking!' }),
                      modal: 'errorToast'
                    })
                  );
                  dispatch(changeOpenedModal({ modal: 'bookingDetailsV2' }));
                }
              })
            );
          },
          fail: () => {
            dispatch(
              setToast({
                toastShow: true,
                toastMessage: t('Failed to get amenity!'),
                modal: 'errorToast'
              })
            );
            dispatch(changeOpenedModal({ modal: 'bookingDetailsV2' }));
          }
        })
      );
    },
    [booking, booking?.building_id, dispatch, globalBuildingId, t]
  );

  const handleEditBlock = useCallback(() => {
    openEditModal('editBlockV2Loading', 'editBlockV2');
  }, [openEditModal]);

  const handleEditBooking = useCallback(() => {
    openEditModal('editBookingV2Loading', 'editBookingV2');
  }, [openEditModal]);

  const refreshBookingsAfterMutation = useCallback(() => {
    const latest = store.getState();
    const calendarSnap = latest.parAmBooking.lastBookingsCalendarV3Params;
    const buildingId = latest.building.globalBuildingId;
    const orgId = latest.Organization.globalOrgId;

    if (calendarSnap && Number(calendarSnap.building) === Number(buildingId)) {
      dispatch(getBookingsCalendarV3(calendarSnap));
    }
    dispatch(
      getBookingsListV3(
        buildListV3Params({
          buildingId,
          page: 1,
          search: latest.parAmBooking.search,
          orgId
        })
      )
    );
  }, [dispatch, store]);

  const handleApproveSuccess = useCallback(() => {
    setIsApproving(false);
    dispatch(
      changeOpenedModal({
        modal: '',
        cancelModal: false
      })
    );
    refreshBookingsAfterMutation();
    dispatch(
      setToast({
        toastShow: true,
        toastMessage: t('Booking approved successfully')
      })
    );
  }, [dispatch, refreshBookingsAfterMutation, t]);

  const handleApproveFail = useCallback(
    (err) => {
      setIsApproving(false);
      dispatch(
        setToast({
          toastShow: true,
          toastMessage: err?.data?.message || t('Failed to approve booking'),
          modal: 'errorToast'
        })
      );
    },
    [dispatch, t]
  );

  const handleApproveBooking = useCallback(() => {
    if (!booking?.id || isApproving) return;
    setIsApproving(true);
    dispatch(
      confirmBookingV3({
        id: booking.id,
        success: handleApproveSuccess,
        fail: handleApproveFail
      })
    );
  }, [booking, dispatch, handleApproveFail, handleApproveSuccess, isApproving]);

  const headerTitle = getBookingDetailHeaderTitle(booking);
  const statusLabel = getBookingDetailStatusLabel(booking);
  const isBlock = isBookingBlock(booking);
  const showApprove = canShowApproveBooking(booking);
  const approveDisabled = isApproveBookingDisabled(booking);
  const showCancel = canShowCancelBooking(booking);
  const showEditBlock = isBlock && canShowEditBooking(booking);
  const showEditBooking = !isBlock && canShowEditBooking(booking);
  const showFooter = showApprove || showCancel || showEditBlock || showEditBooking;

  if (!isOpen) {
    return null;
  }

  return (
    <StyledModalCardV2
      {...BOOKING_DETAIL_MODAL_PROPS}
      show={isOpen}
      onClose={onClose}
      width="555px"
      closeOnBackdrop={true}>
      {bookingPending ? (
        <LoadingAvatar color={LOADING_BLUE} />
      ) : bookingFailed ? (
        <div className="error_for_modal p-4">
          <h4 className="text-danger">{error}</h4>
        </div>
      ) : bookingLoaded ? (
        <>
          <DetailHeaderBlock>
            <DetailHeaderRow>
              <DetailHeaderTitle title={headerTitle}>{headerTitle}</DetailHeaderTitle>
              <DetailHeaderActions>
                <BookingDetailStatusBadge booking={booking} statusLabel={statusLabel} />
                <DetailCloseButton type="button" onClick={onClose} aria-label={t('Close')}>
                  <IoCloseOutline size={16} color={Palette.CONTENT_TERTIARY} />
                </DetailCloseButton>
              </DetailHeaderActions>
            </DetailHeaderRow>
          </DetailHeaderBlock>

          <StyledModalBodyV2 gap="0">
            <BookingDetailsV2Content
              booking={booking}
              bannerUrl={bannerUrl}
              onBannerError={handleBannerError}
              onViewAmenity={handleViewAmenity}
              viewAmenityDisabled={!booking?.amenity_id}
            />
          </StyledModalBodyV2>

          {showFooter ? (
            <CModalFooter>
              {showApprove ? (
                <ApproveBookingButton
                  type="button"
                  onClick={handleApproveBooking}
                  disabled={isApproving || approveDisabled}>
                  {t('Approve Booking')}
                </ApproveBookingButton>
              ) : null}
              {showEditBlock ? (
                <ApproveBookingButton type="button" onClick={handleEditBlock}>
                  {t('Edit Block', { defaultValue: 'Edit Block' })}
                </ApproveBookingButton>
              ) : null}
              {showEditBooking ? (
                <ApproveBookingButton type="button" onClick={handleEditBooking}>
                  {t('Edit Booking')}
                </ApproveBookingButton>
              ) : null}
              {showCancel ? (
                <CancelBookingButton type="button" onClick={handleCancelBooking}>
                  {isBlock
                    ? t('Remove Block', { defaultValue: 'Remove Block' })
                    : t('Cancel Booking')}
                </CancelBookingButton>
              ) : null}
            </CModalFooter>
          ) : null}
        </>
      ) : null}
    </StyledModalCardV2>
  );
};

export default BookingDetailsV2Modal;
